import { AdminPageHeader } from '@/components/patterns/admin-page'
import CreateActionButton from '@/components/patterns/create-action-button'
import {
  compareIndexDate,
  compareIndexNumber,
  compareIndexText,
  getClientIndexPageItems,
  getClientIndexPaginationMeta,
  parseIndexExactIdSearch,
} from '@/components/tables/clientIndexTable'
import {
  IndexDataTable,
  IndexTableClearFiltersButton,
  IndexTableExportButton,
  IndexTablePagination,
  IndexTableSearchBox,
  IndexTableToolbar,
} from '@/components/tables/IndexTable'
import { useIndexTableSpreadsheetExport } from '@/components/tables/indexTableExport'
import { getFloristStatusLabel } from '@/components/organizations/FloristStatusBadge'
import { useIndexTableSearchParams } from '@/components/tables/useIndexTableSearchParams'
import {
  getOrganizationExportColumns,
  getOrganizationIndexColumns,
} from '@/components/tables/organizations/columns'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { usePhoneFormatter } from '@/hooks/use-phone-formatter'
import { useOrganizationContext } from '@/hooks/useOrganizationContext'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can } from '@/lib/casl'
import { useAuthState } from '@/lib/services/auth/auth.query'
import { organizationUrlSearchSchema } from '@/lib/services/organizations/organization.schema'
import {
  organizationQueries,
  useOrganizations,
} from '@/lib/services/organizations/organizations.query'
import { prefetchIndexTableRouteQuery } from '@/lib/services/index-table-query-options'
import {
  createFileRoute,
  Link,
  notFound,
  stripSearchParams,
  useNavigate,
} from '@tanstack/react-router'
import {
  BadgeCheck,
  Building,
  CircleOff,
  SlidersHorizontal,
  User,
  Users,
  Wrench,
} from 'lucide-react'
import { useMemo } from 'react'

const DEFAULT_PAGE_SIZE = 20
const defaultOrganizationListSearchParams = {
  sort: 'name_asc',
  page: 1,
  page_size: DEFAULT_PAGE_SIZE,
}
const strippedOrganizationListSearchParams = {
  sort: defaultOrganizationListSearchParams.sort,
  page: defaultOrganizationListSearchParams.page,
  page_size: defaultOrganizationListSearchParams.page_size,
}
const normalizeDigits = (value = '') => value.replace(/\D/g, '')
const repFilterKeys = ['rep', 'sales_rep', 'support_rep', 'created_by']
const organizationApiFilterKeys = ['rep', 'sales_rep', 'support_rep', 'created_by', 'status']
const statusFilterOptions = [
  {
    value: 'all',
    label: 'All Statuses',
    icon: <SlidersHorizontal className="size-4" />,
  },
  {
    value: 'live',
    label: 'Live Florists',
    icon: <BadgeCheck className="size-4" />,
  },
  {
    value: 'needs_setup',
    label: 'Needs Setup',
    icon: <Wrench className="size-4" />,
  },
  {
    value: 'lost',
    label: 'Lost Florists',
    icon: <CircleOff className="size-4" />,
  },
]
const baseRepFilterOptions = [
  { value: 'all', label: 'All Reps', icon: <Users className="size-4" /> },
  { value: 'me', label: 'Rep: Me', icon: <User className="size-4" /> },
]

function getOrganizationListSearchParams(search) {
  return organizationUrlSearchSchema.parse(search ?? {})
}

function getOrganizationApiFilters(search) {
  const validSearch = getOrganizationListSearchParams(search)

  return Object.fromEntries(
    organizationApiFilterKeys
      .map((key) => [key, validSearch[key]])
      .filter(([, value]) => value !== undefined)
  )
}

function getOrganizationCountText(total) {
  return `${total} organization${total === 1 ? '' : 's'}`
}

function getOrganizationStatusSortValue(org) {
  return getFloristStatusLabel(org.florist_status)
}

function getOrganizationDefaultLocationText(org) {
  const defaultLocation = org.default_location ?? {}

  return [defaultLocation.city, defaultLocation.state].filter(Boolean).join(', ')
}

function sortOrganizations(organizations, sort, prioritizedOrganizationId) {
  const sortedOrganizations = [...(organizations ?? [])]

  sortedOrganizations.sort((left, right) => {
    const leftIsPrioritized =
      prioritizedOrganizationId !== undefined &&
      Number(left.id) === Number(prioritizedOrganizationId)
    const rightIsPrioritized =
      prioritizedOrganizationId !== undefined &&
      Number(right.id) === Number(prioritizedOrganizationId)

    if (leftIsPrioritized !== rightIsPrioritized) {
      return leftIsPrioritized ? -1 : 1
    }

    switch (sort) {
      case 'name_desc':
        return compareIndexText(right.name, left.name)
      case 'status_asc':
        return compareIndexText(
          getOrganizationStatusSortValue(left),
          getOrganizationStatusSortValue(right)
        )
      case 'status_desc':
        return compareIndexText(
          getOrganizationStatusSortValue(right),
          getOrganizationStatusSortValue(left)
        )
      case 'domain_asc':
        return compareIndexText(left.primary_domain, right.primary_domain)
      case 'domain_desc':
        return compareIndexText(right.primary_domain, left.primary_domain)
      case 'fsn_client_id_asc':
        return compareIndexNumber(left.fsn_client_id, right.fsn_client_id)
      case 'fsn_client_id_desc':
        return compareIndexNumber(right.fsn_client_id, left.fsn_client_id)
      case 'phone_asc':
        return compareIndexText(left.default_location?.phone, right.default_location?.phone)
      case 'phone_desc':
        return compareIndexText(right.default_location?.phone, left.default_location?.phone)
      case 'email_asc':
        return compareIndexText(left.default_location?.email, right.default_location?.email)
      case 'email_desc':
        return compareIndexText(right.default_location?.email, left.default_location?.email)
      case 'location_asc':
        return compareIndexText(
          getOrganizationDefaultLocationText(left),
          getOrganizationDefaultLocationText(right)
        )
      case 'location_desc':
        return compareIndexText(
          getOrganizationDefaultLocationText(right),
          getOrganizationDefaultLocationText(left)
        )
      case 'created_asc':
        return compareIndexDate(left.created_at, right.created_at)
      case 'created_desc':
        return compareIndexDate(right.created_at, left.created_at)
      default:
        return compareIndexText(left.name, right.name)
    }
  })

  return sortedOrganizations
}

export function filterOrganizations(organizations, search) {
  const normalizedSearch = search.trim().toLowerCase()

  if (!normalizedSearch) return organizations

  const exactId = parseIndexExactIdSearch(search)

  if (exactId !== null) {
    return organizations?.filter(
      (org) => Number(org.id) === exactId || Number(org.fsn_client_id) === exactId
    )
  }

  const normalizedPhoneSearch = normalizeDigits(search)

  return organizations?.filter((org) => {
    const defaultLocation = org.default_location ?? {}
    const phoneDigits = normalizeDigits(defaultLocation.phone ?? '')
    const searchableValues = [
      org.name,
      org.id?.toString(),
      org.fsn_client_id?.toString(),
      org.primary_domain,
      defaultLocation.phone,
      defaultLocation.email,
      defaultLocation.city,
      defaultLocation.state,
      [defaultLocation.city, defaultLocation.state].filter(Boolean).join(' '),
      [defaultLocation.city, defaultLocation.state].filter(Boolean).join(', '),
    ]

    const matchesText = searchableValues.some((value) =>
      value?.toString().toLowerCase().includes(normalizedSearch)
    )

    const matchesPhone =
      normalizedPhoneSearch.length > 0 && phoneDigits.includes(normalizedPhoneSearch)

    return matchesText || matchesPhone
  })
}

export const Route = createFileRoute('/_authenticated/$orgId/organizations/')({
  component: OrganizationIndex,
  validateSearch: (search) => organizationUrlSearchSchema.parse(search),
  search: {
    middlewares: [stripSearchParams(strippedOrganizationListSearchParams)],
  },
  loaderDeps: ({ search }) => ({ filters: getOrganizationApiFilters(search) }),
  loader: async ({ context, deps: { filters } }) => {
    if (!context.ability.can('list', 'organizations')) throw notFound()

    void prefetchIndexTableRouteQuery(context.queryClient, organizationQueries.index(filters))

    return { isReadOnly: false }
  },
})

export function OrganizationIndex() {
  const navigate = useNavigate()
  const { data: { user } = {} } = useAuthState()
  const { isImpersonating, organization: currentOrganization } = useOrganizationContext()
  const urlSearch = getOrganizationListSearchParams(Route.useSearch())
  const organizationFilters = getOrganizationApiFilters(urlSearch)
  const { data: organizations = [], isPending } = useOrganizations({
    filters: organizationFilters,
  })
  const { formatPhone } = usePhoneFormatter()
  const path = usePathHelper()
  const urlSearchTerm = urlSearch.search ?? ''
  const { updatePage, updatePageSize, updateSearchTerm, updateSort, updateTableParams } =
    useIndexTableSearchParams({
      navigate,
      getSearchParams: getOrganizationListSearchParams,
      searchTerm: urlSearchTerm,
    })
  const hasActiveSearch = urlSearchTerm.trim().length > 0
  const statusFilterValue = urlSearch.status ?? 'all'
  const hasRepFilter = repFilterKeys.some((key) => urlSearch[key] !== undefined)
  const isRepMe = user?.id !== undefined && String(urlSearch.rep) === String(user.id)
  const repFilterValue = isRepMe ? 'me' : hasRepFilter ? 'custom' : 'all'
  const repFilterOptions =
    repFilterValue === 'custom'
      ? [
          ...baseRepFilterOptions,
          {
            value: 'custom',
            label: 'Filtered Rep',
            icon: <User className="size-4" />,
          },
        ]
      : baseRepFilterOptions
  const hasActiveFilters = hasActiveSearch || urlSearch.status !== undefined || hasRepFilter

  const filteredOrganizations = useMemo(
    () => filterOrganizations(organizations, urlSearchTerm),
    [organizations, urlSearchTerm]
  )
  const sortedOrganizations = useMemo(
    () =>
      sortOrganizations(
        filteredOrganizations,
        urlSearch.sort,
        isImpersonating ? currentOrganization?.id : undefined
      ),
    [currentOrganization?.id, filteredOrganizations, isImpersonating, urlSearch.sort]
  )
  const paginationMeta = getClientIndexPaginationMeta({
    total: sortedOrganizations.length,
    page: urlSearch.page,
    pageSize: urlSearch.page_size,
  })
  const visibleOrganizations = getClientIndexPageItems(sortedOrganizations, paginationMeta)
  const columns = useMemo(() => getOrganizationIndexColumns(formatPhone), [formatPhone])
  const exportColumns = useMemo(() => getOrganizationExportColumns(formatPhone), [formatPhone])
  const { exportDisabled, exportPending, handleExport } = useIndexTableSpreadsheetExport({
    columns: exportColumns,
    rows: sortedOrganizations,
    rowCount: sortedOrganizations.length,
    filename: 'organizations.xlsx',
    sheetName: 'Organizations',
  })

  const applySearchFilter = (nextSearch) => updateTableParams(nextSearch)

  const applyRepFilter = (value) => {
    if (value === 'custom' || (value === 'me' && user?.id === undefined)) {
      return
    }

    applySearchFilter({
      rep: value === 'me' ? user.id : undefined,
      sales_rep: undefined,
      support_rep: undefined,
      created_by: undefined,
    })
  }

  const clearFilters = () => {
    applySearchFilter({
      search: undefined,
      status: undefined,
      rep: undefined,
      sales_rep: undefined,
      support_rep: undefined,
      created_by: undefined,
    })
  }

  return (
    <div className="space-y-4">
      <AdminPageHeader
        icon={Building}
        title="Organizations"
        actions={
          <Can do="create" on="organizations">
            <CreateActionButton asChild>
              <Link {...path.organizations.link('create')}>Create Organization</Link>
            </CreateActionButton>
          </Can>
        }
      />
      <IndexDataTable
        mode="client"
        data={visibleOrganizations}
        columns={columns}
        sort={urlSearch.sort}
        onSortChange={updateSort}
        rowLink={(organization) => path.organizations.show(organization.id).link}
        getRowId={(organization) => String(organization.id)}
        isLoading={isPending}
        toolbar={
          <IndexTableToolbar
            primary={
              <IndexTableSearchBox
                value={urlSearchTerm}
                onCommit={updateSearchTerm}
                countText={getOrganizationCountText(sortedOrganizations.length)}
                placeholder="Search organizations..."
              />
            }
            secondary={
              <>
                {hasActiveFilters ? <IndexTableClearFiltersButton onClick={clearFilters} /> : null}
                <Select
                  value={statusFilterValue}
                  onValueChange={(value) =>
                    applySearchFilter({
                      status: value === 'all' ? undefined : value,
                    })
                  }
                >
                  <SelectTrigger
                    aria-label="Filter organization status"
                    selected={statusFilterValue !== 'all'}
                    className="w-fit"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusFilterOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.icon}
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={repFilterValue} onValueChange={applyRepFilter}>
                  <SelectTrigger
                    aria-label="Filter organizations by representative"
                    selected={repFilterValue !== 'all'}
                    className="w-fit"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {repFilterOptions.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        disabled={option.value === 'me' && user?.id === undefined}
                      >
                        {option.icon}
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            }
            actions={
              <IndexTableExportButton
                disabled={exportDisabled}
                isPending={exportPending}
                onClick={handleExport}
              />
            }
          />
        }
        emptyState={<Typography variant="supporting">No organizations found.</Typography>}
        tableClassName="text-sm"
      />
      <IndexTablePagination
        meta={paginationMeta}
        pageSize={urlSearch.page_size}
        onPageChange={updatePage}
        onPageSizeChange={updatePageSize}
      />
    </div>
  )
}
